import { CommodityDetail } from "features/contracts/enums/commodityDetail";
import OverlayButton from "features/shared/components/buttons/OverlayButton";
import IDataFilterService from "features/shared/components/dataFilter/IDataFilterService";
import { DataGridId } from "features/shared/components/dataGrid/DataGridId";
import { formatPrice } from "features/shared/helpers/Formatters";
import { loadPortalConfig } from "features/shared/helpers/modules";
import { getContractsRoute } from "features/shared/helpers/routeHelper";
import { useInjection } from "infrastructure/ioc/ioc.react";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React from "react";
import SummaryInfoDTO from "../dtos/SummaryInfoDTO";
import moment from "moment";
import { InvoiceState } from "features/invoices/enums/invoiceState";
import { containsKey } from "features/shared/helpers/objects";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import InfoItem from "features/shared/components/infoItem/InfoItem";
import { getCommodityIcon } from "features/shared/helpers/Icons";
import { useTranslation } from "react-i18next";
import { Invoices_Route, ProformaInvoices_Route } from "features/shared/router/routes/routes";
import { InvoiceType } from "features/invoices/enums/invoiceType";
import IUrlParam from "features/shared/components/dataFilter/IUrlParam";
import { BoolEnum } from "features/shared/enums/boolEnum";
import ContractCountDTO from "features/dashboard/contracts/dtos/ContractCountDTO";
import { getCommodityColorSuffix } from "features/contracts/helpers/commodityDetailHelper";
import CommodityIcon from "features/shared/components/icons/CommodityIcon";

import styles from './customerStats.module.scss';

const portalConfig = loadPortalConfig();

const inoviceTypeRouteMap: Record<InvoiceType, string> = {
  Invoice: Invoices_Route,
  Proformainvoice: ProformaInvoices_Route,
  Paymentdocument: "",
  Paymenttaxdocument: "",
  Undefined: ""
}

interface Props {
  summaryInfo: SummaryInfoDTO;
  contractCounts: ContractCountDTO[];
}

const CustomerStats = ({ summaryInfo, contractCounts }: Props) => {
  // translations
  const tLabels = useTranslation("customer\\customer").t;
  const tCommodity = useTranslation("contracts\\enums\\commodityDetail").t;

  // DI
  const dataFilterService = useInjection<IDataFilterService>(IocTypes.DataFilterService);

  // helpers
  const getCount = (...counts: (number | undefined)[]) => {
    let result = 0;
    for (const count of counts) {
      if (count !== undefined) {
        result += count;
      }
    }

    return result.toString();
  };

  const storeContractsFilterParams = (commodityDetail: CommodityDetail, isProduction?: boolean) => {
    let dataGridId = portalConfig.useContractualAccounts
      ? DataGridId.contractualAccountsGrid
      : DataGridId.contractGrid;

      let params: IUrlParam[] = [];

      if (commodityDetail) {
        params.push({ key: "CommodityDetail", value: commodityDetail });
      }
      if (isProduction) {
        params.push({ key: "IsProduction", value: BoolEnum.True });
      }
  
      dataFilterService.storeSetParams(dataGridId, params);
  };

  const hasFilledCount = (summaryInfoPropertyName: string): boolean => {
    if (containsKey(summaryInfo, summaryInfoPropertyName)) {
      let count = (summaryInfo as any)[summaryInfoPropertyName];
      return count !== null;
    }

    return false;
  }

  const getContractsCountTranslate = (contractCount: ContractCountDTO) => { 
    let contractsPart = tLabels("customerStats.contracts");
    let commodityPart = tCommodity(contractCount.commodityDetail!);//.toLocaleLowerCase();
    commodityPart = commodityPart.charAt(0).toLocaleLowerCase() + commodityPart.slice(1)
    let productionPart = contractCount.isProduction ? ` ${tLabels("customerStats.productionLabel")}` : "";

    return `${contractsPart} ${commodityPart}${productionPart}`;
  }

  // handlers
  const onClickRequirementsStatsLinkHandler = () => {
    dataFilterService.storeSetParams(DataGridId.requirementGrid, [
      { key: "OpenOnly", value: String(true) },
    ]);
  };  

  const onClickInvoicesStatsLinkHandler = (type: InvoiceType) => {
    const today = moment(moment().subtract(1, "days"), "DD.MM.YYYY")
        .utc(true)
        .toISOString(false);

    let gridParams: IUrlParam[] = [
      {key: "DueDateTo", value: today },
      {key: "InvoiceStates", value: InvoiceState.Active, append:true },
      {key: "InvoiceStates", value: InvoiceState.PartiallyPaid, append:true }
    ]

    if (type == "Invoice") {
      gridParams.push({key: "InvoiceStates", value: InvoiceState.RepaymentSchedule, append:true });
      gridParams.push({key: "InvoiceStates", value: InvoiceState.TransferToInvoice, append:true });
      gridParams.push({key: "CurrencyCode", value: summaryInfo.currency!});
      gridParams.push({key: "AmountFrom", value: (0).toString()});
      gridParams.push({key: "RemainToPayFrom", value: (0).toString()});
    }

    dataFilterService.storeSetParams(dataFilterService.getGridIdByRoute(inoviceTypeRouteMap[type]) ?? "", gridParams);
  };  

  // sub-renders
  const renderContractLink = (contractCount: ContractCountDTO, index: number) => { 
    return (
      <div className="col-sm-auto px-sm-3 pb-3" key={index}>
        <OverlayButton
          to={getContractsRoute()}
          asLink={true}
          onAsLinkClick={() => storeContractsFilterParams(contractCount.commodityDetail!, contractCount.isProduction)}
        >
        <InfoItem
          title={getContractsCountTranslate(contractCount)}
          value={getCount(contractCount.count)}
          iconColorClass={`text-${getCommodityColorSuffix(contractCount.commodityDetail!, contractCount.isProduction)}`}
          renderIcon={() => (
            <CommodityIcon commodityDetail={contractCount.commodityDetail} isProduction={contractCount.isProduction} size="2x"/>
          )}
        />
        </OverlayButton>
      </div>
    );
  }

  const renderContractLinks = () => {
    return contractCounts.map((item, index) => renderContractLink(item, index))
  };

  // render
  return (
    <div className="row no-gutters justify-content-around">
      {renderContractLinks()}
      <RenderWrapper isVisible={() => hasFilledCount("activeRequirementsCount")}>
        <div className="col-sm-auto px-sm-3 pb-3">
          <OverlayButton
            to={"/requirements"}
            asLink={true}
            onAsLinkClick={onClickRequirementsStatsLinkHandler}
          >
            <InfoItem
              title={tLabels("customerStats.activeRequirements")}
              icon="comments"
              value={getCount(summaryInfo.activeRequirementsCount)}
              iconColorClass={styles.requirementsColor}
            />
          </OverlayButton>
        </div>
      </RenderWrapper>
      <div className="col-sm-auto px-sm-3 pb-3">
        <OverlayButton
          to={"/proforma-invoices"}
          asLink={true}
          onAsLinkClick={() => onClickInvoicesStatsLinkHandler("Proformainvoice")}
        >
          <InfoItem
            title={tLabels("customerStats.unpaidAmountProformaInvoices")}
            icon="donate"
            value={`${tLabels("customerStats.per")} ${formatPrice(
              summaryInfo.unpaidAmountProformaInvoices!,
              summaryInfo.currency!,
              2
            )}`}
            iconColorClass={styles.unpaidProformaInvoicesColor}
          />
        </OverlayButton>
      </div>
      <div className="col-sm-auto px-sm-3 pb-3">
        <OverlayButton
          to={"/invoices"}
          asLink={true}
          onAsLinkClick={() => onClickInvoicesStatsLinkHandler("Invoice")}
        >
          <InfoItem
            title={tLabels("customerStats.unpaidAmountInvoices")}
            icon="donate"
            value={`${tLabels("customerStats.per")} ${formatPrice(
              summaryInfo.unpaidAmountInvoices!,
              summaryInfo.currency!,
              2
            )}`}
            iconColorClass={styles.unpaidInvoicesColor}
          />
        </OverlayButton>
      </div>
    </div>
  );
};

export default CustomerStats;
